import {testIdProps} from '@github-ui/test-id-props'
import {
  ArchiveIcon,
  EyeClosedIcon,
  KebabHorizontalIcon,
  NumberIcon,
  PencilIcon,
  TrashIcon,
} from '@primer/octicons-react'
import {ActionList, type ActionListItemProps, ActionMenu, Button} from '@primer/react'
import {Tooltip} from '@primer/react/drafts'
import {useCallback, useMemo} from 'react'

import {ItemType} from '../../api/memex-items/item-type'
import {BoardColumnActionMenuUI} from '../../api/stats/contracts'
import {useArchiveMemexItemsWithConfirmation} from '../../hooks/use-archive-memex-items-with-confirmation'
import {useRemoveMemexItemWithConfirmation} from '../../hooks/use-remove-memex-items-with-id'
import type {MemexItemModel} from '../../models/memex-item-model'
import {Resources} from '../../strings'

interface GroupMenuProps {
  name: string
  isColumn?: boolean
  onRename?: () => void
  onEditDetails?: () => void
  onEditLimit?: () => void
  onHide?: () => void
  onDelete?: () => void
  /** The list of items belonging to this column */
  items: ReadonlyArray<MemexItemModel>
}

const contextTriggerSx = {position: 'sticky', right: 0, mr: 1}

export const GroupMenu = ({
  name,
  isColumn = false,
  onRename,
  onEditLimit,
  onEditDetails,
  onHide,
  onDelete,
  items,
}: GroupMenuProps) => {
  const nonRedactedItems = useMemo(() => items.filter(item => item.contentType !== ItemType.RedactedItem), [items])

  const {openArchiveConfirmationDialog} = useArchiveMemexItemsWithConfirmation()
  const {openRemoveConfirmationDialog} = useRemoveMemexItemWithConfirmation()

  const onArchiveAll = useCallback(() => {
    if (items.length === 0) return
    const itemIds = items.map(item => item.id)
    openArchiveConfirmationDialog(
      itemIds,
      BoardColumnActionMenuUI,
      Resources.archiveGroupItemsConfirmationTitle,
      Resources.archiveGroupItemsConfirmationMessage(items.length),
    )
  }, [openArchiveConfirmationDialog, items])

  const onDeleteAll = useCallback(() => {
    if (items.length === 0) return
    const itemIds = items.map(item => item.id)
    openRemoveConfirmationDialog(itemIds, BoardColumnActionMenuUI, {
      title: Resources.deleteGroupItemsConfirmationTitle,
      content: Resources.deleteGroupItemsConfirmationMessage(items.length),
    })
  }, [openRemoveConfirmationDialog, items])

  const groupActions = []

  if (onRename) groupActions.push(<RenameGroupAction onSelect={onRename} key="rename" />)

  if (onEditLimit) groupActions.push(<EditGroupLimitAction onSelect={onEditLimit} key="editLimit" />)

  if (onEditDetails) groupActions.push(<EditGroupDetailsAction onSelect={onEditDetails} key="editDetails" />)

  if (onHide) groupActions.push(<HideGroupAction onSelect={onHide} key="hide" />)

  if (onDelete) groupActions.push(<DeleteGroupAction onSelect={onDelete} key="delete" />)

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Tooltip type="label" text={Resources.groupHeaderMenu(name, isColumn)}>
          <Button variant="invisible" leadingVisual={KebabHorizontalIcon} size="small" sx={contextTriggerSx} />
        </Tooltip>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay align="end">
        <ActionList>
          <ActionList.Group title="Items">
            <ArchiveItemsAction disabled={nonRedactedItems.length === 0} onSelect={onArchiveAll} />
            <DeleteItemsAction disabled={nonRedactedItems.length === 0} onSelect={onDeleteAll} />
          </ActionList.Group>

          {groupActions.length > 0 && (
            <>
              <ActionList.Divider />
              <ActionList.Group title={isColumn ? 'Column' : 'Group'}>{groupActions}</ActionList.Group>
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

const RenameGroupAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props} {...testIdProps('group-context-menu-rename')}>
    <ActionList.LeadingVisual>
      <PencilIcon />
    </ActionList.LeadingVisual>
    Rename
  </ActionList.Item>
)

const EditGroupLimitAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props} {...testIdProps('group-context-menu-set-column-limit')}>
    <ActionList.LeadingVisual>
      <NumberIcon />
    </ActionList.LeadingVisual>
    Set limit
  </ActionList.Item>
)

const EditGroupDetailsAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props} {...testIdProps('group-context-menu-edit-option')}>
    <ActionList.LeadingVisual>
      <PencilIcon />
    </ActionList.LeadingVisual>
    Edit details
  </ActionList.Item>
)

const HideGroupAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props} {...testIdProps('group-context-menu-hide-column')}>
    <ActionList.LeadingVisual>
      <EyeClosedIcon />
    </ActionList.LeadingVisual>
    Hide from view
  </ActionList.Item>
)

const DeleteGroupAction = (props: ActionListItemProps) => (
  <ActionList.Item variant="danger" {...props} {...testIdProps('group-context-menu-delete')}>
    <ActionList.LeadingVisual>
      <TrashIcon />
    </ActionList.LeadingVisual>
    Delete
  </ActionList.Item>
)

const ArchiveItemsAction = (props: ActionListItemProps) => (
  <ActionList.Item {...props} {...testIdProps('group-context-menu-archive-cards')}>
    <ActionList.LeadingVisual>
      <ArchiveIcon />
    </ActionList.LeadingVisual>
    Archive all
  </ActionList.Item>
)

const DeleteItemsAction = (props: ActionListItemProps) => (
  <ActionList.Item variant="danger" {...props} {...testIdProps('group-context-menu-delete-cards')}>
    <ActionList.LeadingVisual>
      <TrashIcon />
    </ActionList.LeadingVisual>
    Delete all
  </ActionList.Item>
)

try{ GroupMenu.displayName ||= 'GroupMenu' } catch {}
try{ RenameGroupAction.displayName ||= 'RenameGroupAction' } catch {}
try{ EditGroupLimitAction.displayName ||= 'EditGroupLimitAction' } catch {}
try{ EditGroupDetailsAction.displayName ||= 'EditGroupDetailsAction' } catch {}
try{ HideGroupAction.displayName ||= 'HideGroupAction' } catch {}
try{ DeleteGroupAction.displayName ||= 'DeleteGroupAction' } catch {}
try{ ArchiveItemsAction.displayName ||= 'ArchiveItemsAction' } catch {}
try{ DeleteItemsAction.displayName ||= 'DeleteItemsAction' } catch {}